import { useQuery } from "@tanstack/react-query";
import { Radio, Target, Timer, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { fmtSignedUsd, colorForPnl } from "@/lib/format";

const Bar = ({ pct, ok, color, testId }) => (
  <div className="h-1.5 bg-white/[0.06] overflow-hidden" data-testid={testId}>
    <div
      className={`h-full transition-[width] duration-700 ${ok ? "bg-emerald-400" : color}`}
      style={{ width: `${Math.min(100, pct || 0)}%` }}
    />
  </div>
);

/** Borsa API'lerinden gelen GERÇEK uPnL + Loss Recovery ilerlemesi.
 *  Sadece canlı (live_execution) açık pozisyonlarda render edilir. */
export const LiveHedgeStatus = ({ position }) => {
  const enabled = position?.status === "OPEN" && !!position?.live_execution;
  const { data } = useQuery({
    queryKey: ["position-live", position?.id],
    queryFn: () => api.positionLive(position.id),
    refetchInterval: 5000,
    enabled,
  });
  if (!enabled || !data) return null;

  const lr = data.loss_recovery || {};
  const dkx = data.darkex;
  const ctr = data.counter;
  const remain = Math.max(0, (lr.min_hold_seconds || 300) - (lr.held_seconds || 0));
  const mm = String(Math.floor(remain / 60)).padStart(2, "0");
  const ss = String(remain % 60).padStart(2, "0");

  return (
    <div
      data-testid={`live-status-${position.id}`}
      className="px-4 py-3 border-t border-white/[0.06] bg-black/30 space-y-2.5"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
          <Radio className="w-3 h-3 text-emerald-400" /> Borsa Verisi · Canlı uPnL
        </span>
        <div className="flex items-center gap-4 font-mono">
          <span data-testid="live-darkex-upnl" className={colorForPnl(dkx?.unrealized_pnl ?? 0)}>
            DKX {fmtSignedUsd(dkx?.unrealized_pnl ?? 0)}
          </span>
          <span data-testid="live-counter-upnl" className={colorForPnl(ctr?.unrealized_pnl ?? 0)}>
            {position.counter_exchange} {fmtSignedUsd(ctr?.unrealized_pnl ?? 0)}
          </span>
          <span
            data-testid="live-combined-upnl"
            className={`font-semibold ${colorForPnl(data.combined_upnl ?? 0)}`}
          >
            Σ {fmtSignedUsd(data.combined_upnl ?? 0)}
          </span>
        </div>
      </div>

      {(!dkx || !ctr) && (
        <div
          data-testid="live-missing-warning"
          className="flex items-center gap-1.5 text-[10px] text-amber-400"
        >
          <AlertTriangle className="w-3 h-3" />
          {!dkx && "Darkex'te pozisyon görünmüyor. "}
          {!ctr && `${position.counter_exchange}'te pozisyon görünmüyor.`}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            <span className="flex items-center gap-1">
              <Target className="w-3 h-3" /> Zarar Hedefi (${lr.loss_target_usdt ?? 10})
            </span>
            <span
              data-testid="loss-progress-label"
              className={`font-mono ${lr.loss_ok ? "text-emerald-400" : "text-rose-400"}`}
            >
              {lr.loss_ok ? "TAMAM ✓" : `%${(lr.loss_progress_pct ?? 0).toFixed(0)}`}
            </span>
          </div>
          <Bar pct={lr.loss_progress_pct} ok={lr.loss_ok} color="bg-rose-400" testId="loss-progress-bar" />
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            <span className="flex items-center gap-1">
              <Timer className="w-3 h-3" /> Tutma Süresi (5 dk)
            </span>
            <span
              data-testid="hold-progress-label"
              className={`font-mono ${lr.hold_ok ? "text-emerald-400" : "text-amber-400"}`}
            >
              {lr.hold_ok ? "TAMAM ✓" : `${mm}:${ss}`}
            </span>
          </div>
          <Bar pct={lr.hold_progress_pct} ok={lr.hold_ok} color="bg-amber-400" testId="hold-progress-bar" />
        </div>
      </div>
    </div>
  );
};
